import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/lib/i18n/i18n";
import { getAccessiblePages, getRoleColor } from "@/lib/role-permissions";
import { cleRoleAffiche } from "@/lib/user-roles";
import { getPageRoute, getPageIdFromPath } from "@/lib/nav-map";
import { getVisibleSections, getVisibleDashboards } from "@/lib/menu-sections";
import { Church, LogOut, ChevronDown, ChevronLeft, ChevronRight, ShieldCheck, Home, Bell, LifeBuoy, Briefcase, Compass, LayoutGrid } from "lucide-react";
import { gestesDuRole, quotidienDuRole, etiquetteGeste, routeDuGeste } from "@/lib/mon-travail";
import { useModeAffichage } from "@/hooks/useModeAffichage";
import { ParishSwitcher } from "@/components/ParishSwitcher";
import { useMobileNav, closeMobileNav } from "@/hooks/use-mobile-nav";
import { useNotificationsHistory } from "@/hooks/useNotificationsHistory";
import { useAlertesParoisse, voitAlertesParoisse } from "@/hooks/useAlertesParoisse";
import { useAlertesVues, signatureAlerte } from "@/hooks/useAlertesVues";
import { useCurrentParishIdOrNull } from "@/contexts/parish-context";

// Les sections et entrées du menu (manuel 2, page 6) vivent dans
// `lib/menu-sections.ts` ; leurs `labelKey` sont consommés via `t(labelKey)`.
// i18n-dynamic: common.sidebarGroups.*
//
// Deux modes d'affichage (décision PO du 10/10/2026, `useModeAffichage`) :
// - « standard » : le menu de la page 6 du manuel, tel quel ;
// - « travail » : « Mon travail » (les cinq actions du chapitre du rôle dans
//   le manuel), « Au quotidien », puis les sections du manuel repliées sous
//   « Tout l'espace », sans vitrines « à venir » ni doublons.

export function AppSidebar() {
  const t = useTranslation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar-collapsed") === "true"; } catch { return false; }
  });
  const mobileOpen = useMobileNav();
  const [, navigate] = useLocation();
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();

  // Compteur RÉEL de notifications non lues (notification-service) — alimente
  // le badge de l'entrée « Notifications » (page 86). `size: 1` : seul le
  // total `unread` de l'enveloppe nous intéresse, pas la liste.
  const { data: notificationsPage } = useNotificationsHistory({ size: 1 });
  // Manuel 2, Action 03 : le point rouge signale aussi les alertes de la
  // paroisse à traiter dans la journée (curé, conseiller en lecture), mêmes
  // lectures et même cache que le tableau de bord. Seules comptent celles que
  // ce compte n'a pas encore vues dans le centre de notifications (tournage
  // du 11/10/2026 : le point restait à 2 quand le centre disait « Non lues
  // (0) ») ; une alerte nouvelle ou qui change rallume le point.
  const parishId = useCurrentParishIdOrNull();
  const roleAlertes = user?.role || "";
  const { alertes: alertesParoisse } = useAlertesParoisse(parishId, roleAlertes, voitAlertesParoisse(roleAlertes));
  const { vues: alertesVues } = useAlertesVues(user?.id, parishId);
  const alertesDuJourNonVues = alertesParoisse.filter(
    (a) => a.urgence === "jour" && !alertesVues.has(signatureAlerte(a)),
  ).length;
  const unreadNotifications = (notificationsPage?.unread ?? 0) + alertesDuJourNonVues;

  // Sur mobile, le drawer ignore l'état collapsed (toujours full width 280px)
  // et se ferme automatiquement à chaque navigation.
  useEffect(() => {
    closeMobileNav();
  }, [location]);

  // Verrouille le scroll de la page quand le drawer est ouvert (mobile).
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    try { localStorage.setItem("sidebar-collapsed", String(collapsed)); } catch {}
  }, [collapsed]);

  // Groupes repliables (conformité proto DA V05) : état persisté, groupes
  // ouverts par défaut ; le groupe contenant la page active reste ouvert.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem("sidebar-open-groups");
      if (saved) {
        const parsed = JSON.parse(saved) as unknown;
        // Garde : un "null"/tableau/scalaire stocké crasherait le rendu.
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed as Record<string, boolean>;
        }
      }
    } catch {}
    return {};
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar-open-groups", JSON.stringify(openGroups)); } catch {}
  }, [openGroups]);
  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => ({ ...prev, [label]: prev[label] === false }));

  // « Tout l'espace » (mode travail) : choix de la personne s'il existe ;
  // sinon ouvert seulement quand l'écran courant n'est pas déjà en haut.
  const [toutOuvertChoisi, setToutOuvertChoisi] = useState<boolean | null>(() => {
    try {
      const v = localStorage.getItem("sidebar-tout-espace");
      return v === "true" ? true : v === "false" ? false : null;
    } catch { return null; }
  });
  const basculerToutEspace = (ouvert: boolean) => {
    setToutOuvertChoisi(ouvert);
    try { localStorage.setItem("sidebar-tout-espace", String(ouvert)); } catch {}
  };

  const { mode, possible: modeTravailPossible, changer: changerMode } = useModeAffichage(user?.role || "secretaire");

  if (!user) return null;

  const role = user.role || "secretaire";
  const modeTravail = mode === "travail";
  const gestes = modeTravail ? gestesDuRole(role) : [];
  const quotidien = modeTravail ? quotidienDuRole(role) : [];
  const accessibleIds = getAccessiblePages(role);
  const NAV_GROUPS = getVisibleSections(role)
    .map((section) => ({
      ...section,
      label: t(section.labelKey),
      // Mode travail : ni vitrine « à venir », ni seconde occurrence d'un écran.
      pages: section.entries
        .filter((e) => !modeTravail || (!e.comingSoon && !e.testId))
        .map((e) => ({ ...e, label: t(e.labelKey) })),
    }))
    .filter((section) => section.pages.length > 0);
  // Mode travail : un seul tableau de bord par personne, l'accueil.
  const dashboards = modeTravail ? [] : getVisibleDashboards(role);
  const canSeeNotifications = accessibleIds.includes(86);

  const currentPageId = (() => {
    const m = location.match(/\/page\/(\d+)/);
    if (m) return parseInt(m[1]);
    return getPageIdFromPath(location);
  })();

  const goTo = (pageId: number) => navigate(getPageRoute(pageId));

  const handleLogout = () => {
    logoutMutation.mutate(undefined, { onSuccess: () => navigate("/") });
  };

  const initials = (user.fullName || "U")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const roleColor = getRoleColor(role);
  const isDashboard = currentPageId === 2 || location === "/dashboard";
  const pageEnHaut = isDashboard || gestes.some((g) => g.pageId === currentPageId) || quotidien.some((g) => g.pageId === currentPageId);
  const toutOuvert = toutOuvertChoisi ?? !pageEnHaut;

  // Largeur desktop : varie selon collapsed. Mobile : drawer fixé à 280px.
  const desktopWidth = collapsed ? 56 : 220;

  return (
    <>
      {/* Backdrop mobile : overlay sombre qui ferme le drawer au clic.
          Visible uniquement < md ET quand mobileOpen=true. */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileNav}
        aria-hidden="true"
      />

      {/* Sidebar — desktop: inline flex item ; mobile: drawer off-canvas */}
      <div
        data-app-sidebar="true"
        className={`flex flex-col h-screen border-r transition-transform md:transition-all duration-300 z-50
          bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800
          md:relative md:translate-x-0 md:shrink-0
          fixed md:static inset-y-0 left-0 w-[280px] md:w-[var(--sidebar-w)]
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        style={{
          ["--sidebar-w" as any]: `${desktopWidth}px`,
          boxShadow: "1px 0 8px rgba(26,43,74,0.04)",
        }}
      >
      <div className="flex items-center justify-between px-2 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
        {!collapsed && (
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 ml-1 min-w-0"
            data-testid="sidebar-logo"
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg,var(--brand-gold),var(--brand-gold-light))" }}>
              <Church className="w-3.5 h-3.5 text-white"  aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="text-brand-primary dark:text-gray-100 font-black text-sm leading-none">CATHO+</div>
              <div className="text-gray-400 text-[9px]">Paroisses</div>
            </div>
          </button>
        )}
        {collapsed && (
          <button onClick={() => navigate("/dashboard")} className="mx-auto" data-testid="sidebar-logo-mini">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,var(--brand-gold),var(--brand-gold-light))" }}>
              <Church className="w-4 h-4 text-white" />
            </div>
          </button>
        )}
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-all shrink-0"
            title={t("common.reduce")}
            data-testid="sidebar-collapse"
          >
            <ChevronLeft className="w-3.5 h-3.5"  aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="px-2 py-2.5 border-b border-gray-200 dark:border-gray-800 shrink-0">
        {collapsed ? (
          <div className="flex justify-center">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[10px] font-bold"
              style={{ background: `linear-gradient(135deg,${roleColor},${roleColor}cc)` }}
              title={user.fullName}>
              {initials}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-1">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[10px] font-bold shrink-0"
              style={{ background: `linear-gradient(135deg,${roleColor},${roleColor}cc)` }}>
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-brand-primary dark:text-gray-100 text-[11px] font-semibold truncate">{user.fullName}</div>
              <div className="flex items-center gap-1">
                <ShieldCheck className="w-2.5 h-2.5 shrink-0" style={{ color: roleColor }} />
                <span className="text-[9px] font-medium truncate" style={{ color: roleColor }}>{t(`auth.roles.${cleRoleAffiche(role, user.roles)}.label`)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <ParishSwitcher collapsed={collapsed} />

      <div className="flex-1 overflow-y-auto overflow-x-hidden py-2" style={{ scrollbarWidth: "none" }}>

        {/* Section « Dashboard » du manuel : toujours présente. */}
        <div className="px-2 mb-1" data-testid="sidebar-section-dashboard">
          <NavItem
            icon={Home}
            label={t("common.sidebarGroups.dashboard")}
            active={isDashboard}
            collapsed={collapsed}
            onClick={() => goTo(2)}
            color="var(--brand-royal-light)"
            testId="sidebar-home"
          />
          {!collapsed && dashboards.map((d) => (
            <button
              key={d.id}
              onClick={() => goTo(d.id)}
              className={`w-full flex items-center gap-2 pl-9 pr-3 py-1 rounded-lg text-left transition-all mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 ${currentPageId === d.id ? "bg-gray-100 dark:bg-gray-800" : ""}`}
              data-testid={`sidebar-page-${d.id}`}
            >
              <span className="text-[11px] font-medium truncate flex-1 text-[var(--brand-neutral-850)] dark:text-gray-300">
                {t(d.labelKey)}
              </span>
            </button>
          ))}
          {canSeeNotifications && (
            <div className="relative">
              <NavItem
                icon={Bell}
                label={t("common.sidebarGroups.notifications")}
                active={currentPageId === 86}
                collapsed={collapsed}
                onClick={() => goTo(86)}
                color="var(--brand-royal-light)"
                testId="sidebar-page-86"
              />
              {unreadNotifications > 0 && (
                <span
                  className={`absolute flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[8px] font-bold text-white bg-[var(--brand-danger)] ${collapsed ? "top-0.5 right-1" : "top-2 right-3"}`}
                  data-testid="sidebar-notif-badge"
                >
                  {unreadNotifications > 99 ? "99+" : unreadNotifications}
                </span>
              )}
            </div>
          )}
        </div>

        {modeTravail && !collapsed && (
          <>
            <div className="px-2 mb-1" data-testid="sidebar-section-mon-travail">
              <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-0.5">
                <Briefcase className="w-3 h-3 shrink-0 text-[var(--brand-gold)]" aria-hidden="true" />
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--brand-gold)]">
                  {t("common.monTravail.titre")}
                </span>
              </div>
              <p className="px-2 pb-1 text-[9px] text-gray-400" data-testid="sidebar-mon-travail-chapitre">
                {t("common.monTravail.sousTitre", { chapitre: t(`common.monTravail.chapitres.${role}`) })}
              </p>
              {gestes.map((g) => (
                <GesteItem
                  key={g.num}
                  num={g.num}
                  label={t(`common.monTravail.gestes.${g.cle}.titre`)}
                  actionLabel={etiquetteGeste(g, t)}
                  active={currentPageId === g.pageId}
                  onClick={() => navigate(routeDuGeste(g))}
                />
              ))}
            </div>
            {quotidien.length > 0 && (
              <div className="px-2 mb-1" data-testid="sidebar-section-quotidien">
                <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-0.5">
                  <Compass className="w-3 h-3 shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    {t("common.monTravail.quotidien")}
                  </span>
                </div>
                {quotidien.map((g) => (
                  <GesteItem
                    key={g.num}
                    num={g.num}
                    label={t(`common.monTravail.gestes.${g.cle}.titre`)}
                    actionLabel={t("common.monTravail.action", { num: g.num })}
                    active={currentPageId === g.pageId}
                    onClick={() => goTo(g.pageId)}
                  />
                ))}
              </div>
            )}
            <div className="px-2 mt-2 mb-1">
              <button
                type="button"
                onClick={() => basculerToutEspace(!toutOuvert)}
                aria-expanded={toutOuvert}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-dashed border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                data-testid="sidebar-tout-espace"
              >
                <LayoutGrid className="w-3 h-3 shrink-0 text-gray-500" aria-hidden="true" />
                <span className="text-[10px] font-bold uppercase tracking-wide flex-1 text-left text-gray-500">
                  {t("common.monTravail.toutEspace")}
                </span>
                <ChevronDown
                  className="w-3 h-3 shrink-0 text-gray-400 transition-transform duration-200"
                  aria-hidden="true"
                  style={{ transform: toutOuvert ? "rotate(0deg)" : "rotate(-90deg)" }}
                />
              </button>
            </div>
          </>
        )}

        {(!modeTravail || collapsed || toutOuvert) && NAV_GROUPS.map((group) => {
          const visiblePages = group.pages;
          const isGroupActive = visiblePages.some(p => p.id === currentPageId);

          if (collapsed) {
            return (
              <div key={group.key} className="px-1 mb-0.5">
                <button
                  onClick={() => goTo(visiblePages[0].id)}
                  title={group.label}
                  className="w-full flex items-center justify-center py-2 rounded-lg transition-all hover:bg-gray-50"
                  style={{ background: isGroupActive ? `${group.color}10` : "transparent" }}
                  data-testid={`sidebar-group-icon-${group.key}`}
                >
                  <group.icon
                    className="w-4 h-4"
                    style={{ color: isGroupActive ? group.color : "var(--brand-neutral-550)" }}
                  />
                </button>
              </div>
            );
          }

          // Pur état utilisateur (défaut : ouvert). Forcer l'ouverture du
          // groupe actif rendait le clic d'entête inopérant (l'état basculait
          // en silence et ne s'appliquait qu'à la navigation suivante).
          const isOpen = openGroups[group.key] !== false;
          return (
            <div key={group.key} className="px-2 mb-0.5" data-testid={`sidebar-section-${group.key}`}>
              <button
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 mt-1 mb-0.5 rounded-md hover:bg-gray-50 transition-all"
                data-testid={`sidebar-group-toggle-${group.key}`}
                title={isOpen ? t("common.reduce") : t("common.expand")}
              >
                <group.icon className="w-3 h-3 shrink-0" style={{ color: group.color }} />
                <span className="text-[10px] font-bold uppercase tracking-wide flex-1 text-left" style={{ color: group.color }}>
                  {group.label}
                </span>
                <ChevronDown
                  className="w-3 h-3 shrink-0 transition-transform duration-200"
                  aria-hidden="true"
                  style={{
                    color: group.color,
                    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    opacity: 0.6,
                  }}
                />
              </button>
              {isOpen && visiblePages.map((page) => {
                const isActive = currentPageId === page.id;
                return (
                  <button
                    key={page.testId ?? page.id}
                    onClick={() => goTo(page.id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-all mb-0.5 group"
                    style={{
                      background: isActive ? `${group.color}12` : "transparent",
                      borderLeft: isActive ? `2px solid ${group.color}` : "2px solid transparent" }}
                    data-testid={page.testId ?? `sidebar-page-${page.id}`}
                  >
                    <span
                      className="text-[11px] font-medium truncate flex-1"
                      style={{ color: isActive ? "var(--brand-navy)" : "var(--brand-neutral-850)" }}
                    >
                      {page.label}
                    </span>
                    {page.comingSoon && (
                      <span
                        className="shrink-0 rounded-full px-1.5 py-px text-[8px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                        data-testid={`sidebar-coming-soon-${page.id}`}
                      >
                        {t("common.sidebarGroups.comingSoon")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}

        {collapsed && (
          <div className="px-1 mt-2">
            <button
              onClick={() => setCollapsed(false)}
              className="w-full flex items-center justify-center py-2 rounded-lg text-gray-300 hover:text-brand-info-blue hover:bg-gray-50 transition-all"
              title={t("common.expand")}
              data-testid="sidebar-expand"
            >
              <ChevronRight className="w-4 h-4"  aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <div className="px-2 py-3 border-t border-gray-200 dark:border-gray-800 shrink-0">
        {modeTravailPossible && !collapsed && (
          <div className="px-1 pb-2" data-testid="sidebar-mode-affichage">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 px-1 pb-1">
              {t("common.monTravail.modeAffichage")}
            </div>
            <div className="grid grid-cols-2 gap-0.5 p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800" role="group" aria-label={t("common.monTravail.modeAffichage")}>
              {(["travail", "standard"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => changerMode(m)}
                  className={`text-[10px] font-semibold py-1 rounded-md transition-all ${mode === m ? "bg-white dark:bg-gray-900 text-[var(--brand-navy)] dark:text-gray-100 shadow-sm" : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"}`}
                  data-testid={`sidebar-mode-${m}`}
                  title={t(`common.monTravail.modes.${m}.aide`)}
                >
                  {t(`common.monTravail.modes.${m}.label`)}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Action 06 du manuel : « Ouvrez Assistance en bas du menu ». */}
        <NavItem
          icon={LifeBuoy}
          label={t("common.sidebarGroups.assistance")}
          active={location.split("?")[0] === "/assistance"}
          collapsed={collapsed}
          onClick={() => navigate("/assistance")}
          color="var(--brand-royal-light)"
          testId="sidebar-assistance"
        />
        <button aria-label={t("common.logout")}
          onClick={handleLogout}
          className={`w-full flex items-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all ${collapsed ? "justify-center py-2" : "gap-2.5 px-3 py-2"}`}
          title={t("common.logout")}
          data-testid="sidebar-logout"
        >
          <LogOut className="w-4 h-4 shrink-0"  aria-hidden="true" />
          {!collapsed && <span className="text-xs font-medium">{t("common.logout")}</span>}
        </button>
      </div>
      </div>
    </>
  );
}

function NavItem({
  icon: Icon, label, active, collapsed, onClick, color, testId }: {
  icon: React.ElementType;
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
  color: string;
  testId?: string;
}) {
  return (
    <button aria-label={collapsed ? label : undefined}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={`w-full flex items-center rounded-xl transition-all ${collapsed ? "justify-center py-2" : "gap-2.5 px-3 py-2"}`}
      style={{
        background: active ? `${color}12` : "transparent",
        borderLeft: !collapsed && active ? `2px solid ${color}` : "2px solid transparent" }}
      data-testid={testId}
    >
      <Icon className="w-4 h-4 shrink-0" style={{ color: active ? color : "var(--brand-neutral-550)" }}  aria-hidden="true" />
      {!collapsed && (
        <span className="text-xs font-semibold truncate" style={{ color: active ? "var(--brand-navy)" : "var(--brand-neutral-850)" }}>
          {label}
        </span>
      )}
    </button>
  );
}

/** Entrée de « Mon travail » ou « Au quotidien » : titre du manuel et numéro d'action. */
function GesteItem({ num, label, actionLabel, active, onClick }: {
  num: string;
  label: string;
  actionLabel: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-all mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800"
      style={{
        background: active ? "color-mix(in srgb, var(--brand-gold) 14%, transparent)" : undefined,
        borderLeft: active ? "2px solid var(--brand-gold)" : "2px solid transparent" }}
      data-testid={`sidebar-geste-${num}`}
    >
      <span className="text-[11px] font-medium truncate flex-1 text-[var(--brand-neutral-850)] dark:text-gray-200">
        {label}
      </span>
      <span className="shrink-0 text-[8px] font-bold tabular-nums text-[var(--brand-gold)]">{actionLabel}</span>
    </button>
  );
}
